import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CategoryTooltip from '../components/CategoryTooltip';
import LoadingIndicator from '../components/LoadingIndicator';
import ProductCarousel from '../components/ProductCarousel';
import { fetchCategories } from '../services/category.api';
import { searchLocations } from '../services/location.api';
import { fetchCounties, forgetCachedCounties, isUnknownCountyError, readCachedCounties } from '../services/countyCache';
import { Category, County, Location } from '../services/api.types';
import { ASSETS } from '../constants/assets';
import { useTranslations } from '../translations';
import { appLinkClick, createPageUrl } from '../utils/appRoutes';
import './Home.css';
import '../components/ThemedDropdown.css';

interface SearchParams {
  query?: string;
  location?: string;
  locationId?: string;
  categoryId?: string;
  subCategoryId?: string;
  productType?: 'PRODUCT' | 'DEMAND';
}

interface HomeProps {
  isLoggedIn: boolean;
  isAdmin?: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToSearch: (params: SearchParams) => void;
  onNavigateToProductDetail: (productId: string) => void;
  onLogout: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToAdministration?: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToContact?: () => void;
}

const isMobileCategoryViewport = () => {
  const { innerWidth, innerHeight } = window;
  return innerWidth <= 768
    || (innerWidth <= 1024 && innerHeight <= 500 && innerWidth > innerHeight);
};

const Home: React.FC<HomeProps> = ({ isLoggedIn, isAdmin = false, onNavigateToLogin, onNavigateToRegister, onNavigateToSearch, onNavigateToProductDetail, onLogout, onNavigateToProfile, onNavigateToMyAnnouncements, onNavigateToLocations, onNavigateToSettings, onNavigateToCreateProduct, onNavigateToMessages, onNavigateToAdministration, onNavigateToTerms, onNavigateToPrivacy, onNavigateToCookies, onNavigateToAbout, onNavigateToContact }) => {
  const t = useTranslations();
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchType, setSearchType] = useState<'PRODUCT' | 'DEMAND'>('PRODUCT');
  const [itemsPerRow, setItemsPerRow] = useState(6);
  const [isMobileCategoryLayout, setIsMobileCategoryLayout] = useState(isMobileCategoryViewport);
  const [activeCategoryPage, setActiveCategoryPage] = useState(0);

  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [counties, setCounties] = useState<County[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<County | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const locationInputRef = useRef<HTMLDivElement>(null);
  const locationRequestIdRef = useRef(0);
  const [countySearchQuery, setCountySearchQuery] = useState('');
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [searchingWholeCounty, setSearchingWholeCounty] = useState(false);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const categorySliderRef = useRef<HTMLDivElement>(null);
  const [activeTooltip, setActiveTooltip] = useState<'sell' | 'buy' | null>(null);

  const handleTooltipToggle = (tooltip: 'sell' | 'buy', e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveTooltip(activeTooltip === tooltip ? null : tooltip);
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data.content);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    // Preload counties on page load (don't show dropdown)
    loadCounties(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateItemsPerRow = () => {
      if (isMobileCategoryViewport()) {
        setItemsPerRow(3);
        setIsMobileCategoryLayout(true);
        setSelectedCategory(null);
      } else {
        setItemsPerRow(6);
        setIsMobileCategoryLayout(false);
      }
    };

    updateItemsPerRow();
    window.addEventListener('resize', updateItemsPerRow);
    window.addEventListener('orientationchange', updateItemsPerRow);
    window.visualViewport?.addEventListener('resize', updateItemsPerRow);
    return () => {
      window.removeEventListener('resize', updateItemsPerRow);
      window.removeEventListener('orientationchange', updateItemsPerRow);
      window.visualViewport?.removeEventListener('resize', updateItemsPerRow);
    };
  }, []);

  useEffect(() => {
    setActiveCategoryPage(0);
    if (categorySliderRef.current) {
      categorySliderRef.current.scrollLeft = 0;
    }
  }, [categories.length, isMobileCategoryLayout]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (locationInputRef.current && !locationInputRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
      if (categoriesRef.current && !categoriesRef.current.contains(event.target as Node)) {
        setSelectedCategory(null);
      }
      const target = event.target as HTMLElement;
      if (!target.closest('.search-tab-info')) {
        setActiveTooltip(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (location.length >= 3 && !selectedLocation && !searchingWholeCounty) {
      const debounceTimer = setTimeout(() => {
        loadLocationsByName(location);
      }, 300);

      return () => clearTimeout(debounceTimer);
    }
  }, [location, selectedLocation, searchingWholeCounty]);

  const loadCounties = async (shouldShowDropdown = true) => {
    const requestId = ++locationRequestIdRef.current;
    const showCounties = (loadedCounties: County[]) => {
      if (requestId !== locationRequestIdRef.current) {
        return;
      }
      setCounties(loadedCounties);
      setLocations([]);
      setSelectedCounty(null);
      if (shouldShowDropdown) {
        setShowLocationDropdown(true);
      }
    };

    const cachedCounties = readCachedCounties();
    if (cachedCounties) {
      showCounties(cachedCounties);
      return;
    }

    setLocationLoading(true);
    try {
      showCounties(await fetchCounties());
    } catch (error) {
      console.error('Error fetching counties:', error);
    } finally {
      if (requestId === locationRequestIdRef.current) {
        setLocationLoading(false);
      }
    }
  };

  const loadLocationsByCounty = async (county: County) => {
    const requestId = ++locationRequestIdRef.current;
    setLocationLoading(true);
    setSelectedCounty(county);
    try {
      const data = await searchLocations(1, 500, county.id, null);
      if (requestId === locationRequestIdRef.current) {
        setLocations(data.locations.content);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      if (isUnknownCountyError(error)) {
        // The cached county list is from an older database: drop it and show the current one.
        forgetCachedCounties();
        if (requestId === locationRequestIdRef.current) {
          void loadCounties();
          return;
        }
      }
      if (requestId === locationRequestIdRef.current) {
        setLocations([]);
      }
    } finally {
      if (requestId === locationRequestIdRef.current) {
        setLocationLoading(false);
      }
    }
  };

  const loadLocationsByName = async (searchName: string) => {
    const requestId = ++locationRequestIdRef.current;
    setLocationLoading(true);
    try {
      const data = await searchLocations(1, 500, null, searchName);
      if (requestId === locationRequestIdRef.current) {
        setCounties([]);
        setLocations(data.locations.content);
        setSelectedCounty(null);
        setShowLocationDropdown(true);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      if (requestId === locationRequestIdRef.current) {
        setLocationLoading(false);
      }
    }
  };

  const handleLocationInputFocus = () => {
    if (location.length < 3) {
      if (counties.length > 0) {
        // Counties already loaded, just show dropdown
        setShowLocationDropdown(true);
      } else {
        // Load counties and show dropdown
        loadCounties();
      }
    }
  };

  const handleLocationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    locationRequestIdRef.current += 1;
    setLocation(value);
    setSelectedLocation(null);
    setSelectedCounty(null);
    setSearchingWholeCounty(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');

    if (value.length === 0) {
      setShowLocationDropdown(true);
      loadCounties();
    } else if (value.length < 3) {
      setShowLocationDropdown(false);
    }
  };

  const handleCountySelect = (county: County) => {
    loadLocationsByCounty(county);
  };

  const handleLocationSelect = (locationItem: Location) => {
    locationRequestIdRef.current += 1;
    setSelectedLocation(locationItem);
    setLocation(`${locationItem.diacriticsName}, ${locationItem.county.name}`);
    setShowLocationDropdown(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');
    setSearchingWholeCounty(false);
  };

  const handleShowAllFromCounty = (county: County) => {
    locationRequestIdRef.current += 1;
    setSelectedCounty(county);
    setLocation(county.name);
    setSelectedLocation(null);
    setShowLocationDropdown(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');
    setSearchingWholeCounty(true);

    onNavigateToSearch({
      query: searchQuery.trim() || undefined,
      location: county.name || undefined,
      locationId: county.id,
      productType: searchType
    });
  };

  const handleClearLocation = () => {
    locationRequestIdRef.current += 1;
    setLocation('');
    setCounties([]);
    setLocations([]);
    setSelectedLocation(null);
    setSelectedCounty(null);
    setSearchingWholeCounty(false);
    setCountySearchQuery('');
    setLocationSearchQuery('');
    setShowLocationDropdown(false);
    setLocationLoading(false);
  };

  const handleCountySearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCountySearchQuery(value);

    if (value.length === 0 && !selectedCounty) {
      loadCounties();
    }
  };

  const handleLocationSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocationSearchQuery(value);

    if (value.length === 0 && selectedCounty) {
      loadLocationsByCounty(selectedCounty);
    }
  };

  const filteredCounties = counties.filter(county =>
    county.name.toLowerCase().includes(countySearchQuery.trim().toLowerCase())
  );

  const filteredLocations = locations.filter(loc => {
    const searchLower = locationSearchQuery.trim().toLowerCase();
    return loc.name.toLowerCase().includes(searchLower) ||
           loc.diacriticsName.toLowerCase().includes(searchLower);
  });

  const handleSearch = () => {
    let locationId: string | undefined = undefined;

    if (searchingWholeCounty && selectedCounty) {
      locationId = selectedCounty.id;
    } else if (selectedLocation) {
      locationId = selectedLocation.id;
    }

    onNavigateToSearch({
      query: searchQuery.trim() || undefined,
      location: location || undefined,
      locationId: locationId,
      productType: searchType
    });
  };

  const getCategoryIcon = (category: Category) => {
    return (hoveredCategory === category.id || selectedCategory === category.id) ? category.iconHighlightedUrl : category.iconUrl;
  };



  const handleViewAll = (categoryId: string) => {
    onNavigateToSearch({
      categoryId: categoryId,
      productType: searchType,
      query: searchQuery.trim() || undefined,
      location: location || undefined,
      locationId: selectedLocation?.id || undefined
    });
  };

  const handleSubcategoryClick = (subcategoryId: string, categoryId: string) => {
    onNavigateToSearch({
      categoryId: categoryId,
      subCategoryId: subcategoryId,
      productType: searchType,
      query: searchQuery.trim() || undefined,
      location: location || undefined,
      locationId: selectedLocation?.id || undefined
    });
  };

  const handleCategoryClick = (categoryId: string) => {
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      // On mobile, go directly to search results with category filter
      onNavigateToSearch({
        categoryId: categoryId,
        productType: searchType,
        query: searchQuery.trim() || undefined,
        location: location || undefined,
        locationId: selectedLocation?.id || (searchingWholeCounty && selectedCounty ? selectedCounty.id : undefined)
      });
    } else {
      // On desktop, show subcategories dropdown
      setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
      setHoveredCategory(null);
    }
  };

  const renderCategoryItem = (category: Category) => (
    <a
      key={category.id}
      href={createPageUrl('category', { id: category.id, name: category.name })}
      className={`category-item ${selectedCategory === category.id ? 'active' : ''}`}
      aria-expanded={!isMobileCategoryLayout && selectedCategory === category.id}
      aria-controls={!isMobileCategoryLayout && selectedCategory === category.id ? `subcategory-panel-${category.id}` : undefined}
      onMouseEnter={() => setHoveredCategory(category.id)}
      onMouseLeave={() => setHoveredCategory(null)}
      onClick={(event) => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
          return;
        }
        event.preventDefault();
        handleCategoryClick(category.id);
      }}
    >
      <div className="category-icon-wrapper">
        <img
          src={getCategoryIcon(category)}
          alt=""
          className="category-icon-img"
        />
      </div>
      <div className="category-name">
        <span>{category.name}</span>
      </div>
    </a>
  );

  const mobileCategoryPages: Category[][] = [];
  for (let index = 0; index < categories.length; index += 9) {
    mobileCategoryPages.push(categories.slice(index, index + 9));
  }

  const goToCategoryPage = (page: number) => {
    const track = categorySliderRef.current;
    if (!track) return;
    const nextPage = Math.max(0, Math.min(page, mobileCategoryPages.length - 1));
    setActiveCategoryPage(nextPage);
    track.scrollTo({ left: nextPage * track.clientWidth, behavior: 'smooth' });
  };

  const updateCategoryPageFromScroll = () => {
    const track = categorySliderRef.current;
    if (!track || track.clientWidth === 0) return;
    setActiveCategoryPage(Math.min(
      Math.round(track.scrollLeft / track.clientWidth),
      mobileCategoryPages.length - 1,
    ));
  };

  return (
    <div className="home-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={() => {}}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onAdministrationClick={onNavigateToAdministration}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />
      
      <main className="main-content">
        <section className="hero-section">
          <img src={ASSETS.HERO} alt="Hero background" className="hero-image" />
          <div className="hero-overlay">
            <div className="hero-copy">
              <p className="hero-eyebrow">{t.home.heroEyebrow}</p>
              <h1 className="hero-title">{t.home.heroTitle}</h1>
              <p className="hero-description">{t.home.heroDescription}</p>
            </div>

            <div className="search-tabs">
              <span
                className={`search-tab ${searchType === 'PRODUCT' ? 'active' : ''}`}
                onClick={() => setSearchType('PRODUCT')}
              >
                <span className="search-tab-text">{t.home.searchTabSell}</span>
                <span
                  className="search-tab-info"
                  onClick={(e) => handleTooltipToggle('sell', e)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14ZM8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z" fill="#596255"/>
                    <path d="M8 7C8.27614 7 8.5 7.22386 8.5 7.5V11C8.5 11.2761 8.27614 11.5 8 11.5C7.72386 11.5 7.5 11.2761 7.5 11V7.5C7.5 7.22386 7.72386 7 8 7Z" fill="#596255"/>
                    <path d="M8 6C8.41421 6 8.75 5.66421 8.75 5.25C8.75 4.83579 8.41421 4.5 8 4.5C7.58579 4.5 7.25 4.83579 7.25 5.25C7.25 5.66421 7.58579 6 8 6Z" fill="#596255"/>
                  </svg>
                  <span className={`search-tab-tooltip ${activeTooltip === 'sell' ? 'active' : ''}`}>{t.home.searchTabSellTooltip}</span>
                </span>
              </span>
              <span
                className={`search-tab ${searchType === 'DEMAND' ? 'active' : ''}`}
                onClick={() => setSearchType('DEMAND')}
              >
                <span className="search-tab-text">{t.home.searchTabBuy}</span>
                <span
                  className="search-tab-info"
                  onClick={(e) => handleTooltipToggle('buy', e)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14ZM8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z" fill="#596255"/>
                    <path d="M8 7C8.27614 7 8.5 7.22386 8.5 7.5V11C8.5 11.2761 8.27614 11.5 8 11.5C7.72386 11.5 7.5 11.2761 7.5 11V7.5C7.5 7.22386 7.72386 7 8 7Z" fill="#596255"/>
                    <path d="M8 6C8.41421 6 8.75 5.66421 8.75 5.25C8.75 4.83579 8.41421 4.5 8 4.5C7.58579 4.5 7.25 4.83579 7.25 5.25C7.25 5.66421 7.58579 6 8 6Z" fill="#596255"/>
                  </svg>
                  <span className={`search-tab-tooltip search-tab-tooltip-buy ${activeTooltip === 'buy' ? 'active' : ''}`}>{t.home.searchTabBuyTooltip}</span>
                </span>
              </span>
            </div>

            <div className="search-container">
              <div className="search-input-wrapper">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17.5 17.5L13.875 13.875" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="text"
                  placeholder={t.home.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    className="search-clear-button"
                    onClick={() => setSearchQuery('')}
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
              
              <div className="location-input-wrapper" ref={locationInputRef}>
                <img src={ASSETS.PIN} alt="Location" className="location-pin-icon" />
                <input
                  type="text"
                  placeholder={t.home.locationPlaceholder}
                  value={location}
                  onChange={handleLocationInputChange}
                  onFocus={handleLocationInputFocus}
                  className="location-input"
                />
                <button
                  className={`location-clear-button ${location ? '' : 'location-clear-button-placeholder'}`}
                  onClick={handleClearLocation}
                  type="button"
                  disabled={!location}
                  aria-hidden={!location}
                  tabIndex={location ? 0 : -1}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  className="location-dropdown-toggle"
                  onClick={() => {
                    if (showLocationDropdown) {
                      setShowLocationDropdown(false);
                    } else if (counties.length > 0 || locations.length > 0) {
                      setShowLocationDropdown(true);
                    } else {
                      loadCounties();
                    }
                  }}
                  type="button"
                >
                  <svg className={`location-dropdown-icon themed-dropdown-chevron ${showLocationDropdown ? 'location-dropdown-icon-open themed-dropdown-chevron-open' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.9999 16C11.7719 16 11.5449 15.923 11.3599 15.768L5.35991 10.768C4.93591 10.415 4.87791 9.78399 5.23191 9.35999C5.58491 8.93599 6.21491 8.87899 6.63991 9.23199L12.0109 13.708L17.3729 9.39299C17.8029 9.04699 18.4329 9.11499 18.7789 9.54499C19.1249 9.97499 19.0569 10.604 18.6269 10.951L12.6269 15.779C12.4439 15.926 12.2219 16 11.9999 16Z" fill="#071105"/>
                  </svg>
                </button>

                {showLocationDropdown && (
                  <div className="location-dropdown themed-dropdown-menu">
                      {locationLoading ? (
                        <div className="location-dropdown-loading">Loading...</div>
                      ) : (
                        <>
                          {counties.length > 0 && !selectedCounty && (
                          <div className="location-dropdown-list">
                            <div className="location-dropdown-search">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 14L11.1 11.1" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <input
                                type="text"
                                placeholder={t.home.searchCounty}
                                value={countySearchQuery}
                                onChange={handleCountySearchChange}
                                className="location-search-input"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            {filteredCounties.map((county) => (
                              <div
                                key={county.id}
                                className="location-dropdown-item county-item themed-dropdown-option"
                                onClick={() => handleCountySelect(county)}
                              >
                                {county.name}
                              </div>
                            ))}
                            {filteredCounties.length === 0 && (
                              <div className="location-dropdown-empty">Nu s-au găsit rezultate</div>
                            )}
                          </div>
                        )}

                        {selectedCounty && (
                          <>
                            <div className="location-dropdown-counties">
                              {counties.map((county) => (
                                <div
                                  key={county.id}
                                  className={`location-dropdown-item county-item themed-dropdown-option ${selectedCounty.id === county.id ? 'selected themed-dropdown-option-active' : ''}`}
                                  onClick={() => handleCountySelect(county)}
                                >
                                  {county.name}
                                </div>
                              ))}
                            </div>

                            {locations.length > 0 && (
                              <div className="location-dropdown-cities">
                                <div className="location-dropdown-header">
                                  <div
                                    className="location-dropdown-show-all themed-dropdown-option"
                                    onClick={() => handleShowAllFromCounty(selectedCounty)}
                                  >
                                    Arată tot din {selectedCounty.name}
                                  </div>
                                  <div className="location-dropdown-search">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M14 14L11.1 11.1" stroke="#596255" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <input
                                      type="text"
                                      placeholder={t.home.searchLocation}
                                      value={locationSearchQuery}
                                      onChange={handleLocationSearchChange}
                                      className="location-search-input"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                </div>
                                {filteredLocations.map((locationItem) => (
                                  <div
                                    key={locationItem.id}
                                    className={`location-dropdown-item themed-dropdown-option ${selectedLocation?.id === locationItem.id ? 'themed-dropdown-option-active' : ''}`}
                                    onClick={() => handleLocationSelect(locationItem)}
                                  >
                                    {locationItem.diacriticsName}
                                  </div>
                                ))}
                                {filteredLocations.length === 0 && (
                                  <div className="location-dropdown-empty">Nu s-au găsit rezultate</div>
                                )}
                              </div>
                            )}
                          </>
                        )}

                        {!selectedCounty && locations.length > 0 && (
                          <div className="location-dropdown-list">
                            {locations.map((locationItem) => (
                              <div
                                key={locationItem.id}
                                className={`location-dropdown-item themed-dropdown-option ${selectedLocation?.id === locationItem.id ? 'themed-dropdown-option-active' : ''}`}
                                onClick={() => handleLocationSelect(locationItem)}
                              >
                                {locationItem.diacriticsName}, {locationItem.county.name}
                              </div>
                            ))}
                          </div>
                        )}

                        {!locationLoading && counties.length === 0 && locations.length === 0 && !selectedLocation && !searchingWholeCounty && location.length >= 3 && (
                          <div className="location-dropdown-empty">Nu am găsit rezultate</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              <button className="search-button" onClick={handleSearch}>
                {t.home.searchButton}
              </button>
            </div>
          </div>
        </section>

        <section className={`categories-section${isMobileCategoryLayout ? ' categories-section-mobile' : ''}`}>
          <h2 className="section-title">{t.home.categoriesTitle}</h2>

          {loading ? (
            <LoadingIndicator
              message={t.home.loadingCategories}
              variant="section"
            />
          ) : (
            <div className="categories-container" ref={categoriesRef}>
              {isMobileCategoryLayout ? (
                <div
                  className="categories-mobile-carousel"
                  role="region"
                  aria-label={t.home.categoriesCarousel}
                >
                  <div
                    className="categories-mobile-track"
                    ref={categorySliderRef}
                    onScroll={updateCategoryPageFromScroll}
                  >
                    {mobileCategoryPages.map((page, pageIndex) => (
                      <div
                        className="categories-mobile-page"
                        key={pageIndex}
                        role="group"
                        aria-label={t.home.categoryPage
                          .replace('{page}', String(pageIndex + 1))
                          .replace('{total}', String(mobileCategoryPages.length))}
                      >
                        {page.map(renderCategoryItem)}
                      </div>
                    ))}
                  </div>

                  {mobileCategoryPages.length > 1 && (
                    <div className="categories-mobile-navigation">
                      <button
                        type="button"
                        className="categories-mobile-arrow"
                        onClick={() => goToCategoryPage(activeCategoryPage - 1)}
                        disabled={activeCategoryPage === 0}
                        aria-label={t.home.previousCategories}
                      >
                        <ChevronLeft size={19} aria-hidden />
                      </button>
                      <div className="categories-mobile-dots">
                        {mobileCategoryPages.map((_, pageIndex) => (
                          <button
                            type="button"
                            className={`categories-mobile-dot ${pageIndex === activeCategoryPage ? 'active' : ''}`}
                            key={pageIndex}
                            onClick={() => goToCategoryPage(pageIndex)}
                            aria-label={t.home.categoryPage
                              .replace('{page}', String(pageIndex + 1))
                              .replace('{total}', String(mobileCategoryPages.length))}
                            aria-current={pageIndex === activeCategoryPage ? 'page' : undefined}
                          />
                        ))}
                      </div>
                      <button
                        type="button"
                        className="categories-mobile-arrow"
                        onClick={() => goToCategoryPage(activeCategoryPage + 1)}
                        disabled={activeCategoryPage === mobileCategoryPages.length - 1}
                        aria-label={t.home.nextCategories}
                      >
                        <ChevronRight size={19} aria-hidden />
                      </button>
                    </div>
                  )}
                </div>
              ) : (() => {
                const rows: Category[][] = [];
                for (let i = 0; i < categories.length; i += itemsPerRow) {
                  rows.push(categories.slice(i, i + itemsPerRow));
                }

                const selectedCategoryIndex = categories.findIndex(c => c.id === selectedCategory);
                const selectedRowIndex = selectedCategoryIndex >= 0 ? Math.floor(selectedCategoryIndex / itemsPerRow) : -1;

                return rows.map((row, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    <div className="categories-row">
                      {row.map(renderCategoryItem)}
                    </div>

                    {selectedCategory && selectedRowIndex === rowIndex && (
                      <div
                        id={`subcategory-panel-${selectedCategory}`}
                        className="category-tooltip-container"
                        onMouseEnter={() => setHoveredCategory(selectedCategory)}
                        onMouseLeave={() => setHoveredCategory(null)}
                      >
                        <CategoryTooltip
                          category={categories.find(c => c.id === selectedCategory)!}
                          onViewAll={handleViewAll}
                          onSubcategoryClick={handleSubcategoryClick}
                        />
                      </div>
                    )}
                  </React.Fragment>
                ));
              })()}
            </div>
          )}
        </section>

        <ProductCarousel
          title={t.home.announcementsTitle}
          productType={searchType}
          onNavigateToProductDetail={onNavigateToProductDetail}
          isLoggedIn={isLoggedIn}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToSearch={() => onNavigateToSearch({ productType: searchType })}
        />

        <section className="about-section">
          <div className="about-content">
            <h2 className="about-section-title">{t.home.aboutTitle}</h2>

            <p className="about-text">
              {t.home.aboutPlatformText.split('\n').map((line, index) => (
                <span key={index}>
                  {line}
                  {index < t.home.aboutPlatformText.split('\n').length - 1 && <br />}
                </span>
              ))}
            </p>

            <p className="about-text">
              Mai multe informații puteți găsi în secțiunea{' '}
              <a
                className="about-link"
                href={createPageUrl('about')}
                onClick={appLinkClick(onNavigateToAbout)}
              >
                despre noi
              </a>
            </p>

            <button
              className="btn-add-announcement"
              onClick={() => {
                if (isLoggedIn) {
                  onNavigateToCreateProduct?.();
                } else {
                  onNavigateToLogin();
                }
              }}
            >
              {t.home.addAnnouncementButton}
            </button>
          </div>
          <img src={ASSETS.HOME_BOTTOM} alt="Graphic" className="about-graphic" />
        </section>

        <Footer
          onNavigateToTerms={onNavigateToTerms}
          onNavigateToPrivacy={onNavigateToPrivacy}
          onNavigateToCookies={onNavigateToCookies}
          onNavigateToAbout={onNavigateToAbout}
          onNavigateToContact={onNavigateToContact}
        />
      </main>
    </div>
  );
};

export default Home;
